import { config } from '../config/environment';
import { logger } from './logger';

const AUTOMATE_API_BASE =
  config.automateServiceBase ||
  '';

const AUTOMATE_FUNCTIONS_KEY = process.env.REACT_APP_AUTOMATE_API_KEY || 'qmmhZL8BdaqcJqV1_pADAXEVi-V-37li8iVbV8_3pTuzAzFuq-vNUA==';

const getAccessToken = async () => {
  // First try to use the global token getter set up by useAuthenticatedApi hook
  if (typeof window !== 'undefined' && window.getAccessToken) {
    try {
      // Determine the correct scope based on environment
      const scope = config.isProduction 
        ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/automate-service.access_as_user`
        : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/automate-service.access_as_user`;
      
      return await window.getAccessToken(scope);
    } catch (error) {
      logger.warn('Global getAccessToken failed, falling back to direct MSAL:', error);
    }
  }
  
  // Fallback to direct MSAL access for non-React contexts
  const { msalInstance } = await import('../config/msalConfig');
  
  // Determine the correct scope based on environment
  const environment = process.env.NODE_ENV || 'development';
  const scope = environment === 'production' 
    ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/automate-service.access_as_user`
    : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/automate-service.access_as_user`;
  
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    throw new Error('No authenticated user found. Please log in first.');
  }
  
  const account = accounts[0];
  
  try {
    // Try to get token silently first with automate-specific scope
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    // If silent acquisition fails, try interactive method
    if (error.errorCode === 'consent_required' || 
        error.errorCode === 'interaction_required' || 
        error.errorCode === 'login_required') {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [scope],
        });
        return response.accessToken;
      } catch (interactiveError) {
        throw new Error(`Failed to acquire access token: ${interactiveError.message}`);
      }
    }
    throw new Error(`Failed to acquire access token: ${error.message}`);
  }
};

export const startAutomation = async ({ canvassId, numberOfInterviews = 1, customPromptInjection = '' } = {}) => {
  if (!AUTOMATE_API_BASE) {
    throw new Error('Automate service base URL is not configured');
  }

  if (!canvassId) {
    throw new Error('canvassId is required to start automation');
  }

  // Get access token for authentication
  const accessToken = await getAccessToken();

  const fetchPromise = fetch(AUTOMATE_API_BASE, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'x-functions-key': AUTOMATE_FUNCTIONS_KEY,
    },
    body: JSON.stringify({
      gameId: canvassId,
      environment: config.environment || 'staging',
      numberOfInterviews,
      customPromptInjection,
    }),
  });

  const timeoutPromise = new Promise((resolve) => {
    setTimeout(() => resolve({ timedOut: true }), 2000);
  });

  const winner = await Promise.race([fetchPromise, timeoutPromise]);

  if (winner && winner.timedOut) {
    void fetchPromise
      .then(async (res) => {
        if (!res.ok) {
          let data = null;
          try {
            data = await res.json();
          } catch {
            data = null;
          }
          const message = data?.error || data?.message || `Automate request failed (${res.status})`;
          // eslint-disable-next-line no-console
          logger.error(message);
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        logger.error('Failed to start automation:', e);
      });

    return { ok: true, pending: true };
  }

  const res = winner;
  if (!res.ok) {
    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    const message = data?.error || data?.message || `Automate request failed (${res.status})`;
    throw new Error(message);
  }

  return { ok: true, pending: false };
};


